/**
 * Reading a supplier's price list in the browser: find the header row, guess
 * which column is which, and turn sheet rows into the objects the import API
 * takes. The server does the real parsing and validation.
 */

export const FIELDS = [
  { key: 'sku', label: 'Product code', hint: 'Their stock code or SKU. Next week’s list matches on it.' },
  { key: 'name', label: 'Product name', required: true },
  { key: 'unitPrice', label: 'Price (USD)', required: true },
  { key: 'unit', label: 'Unit', hint: 'bag, each, m², litre… Leave unmapped to use the default below.' },
  { key: 'category', label: 'Category' },
  { key: 'availableQuantity', label: 'Quantity in stock' },
  { key: 'inStock', label: 'In stock (yes/no)' },
  { key: 'description', label: 'Description' },
  { key: 'imageUrl', label: 'Photo link' },
];

const SYNONYMS = {
  sku: ['sku', 'code', 'item code', 'product code', 'stock code', 'item no', 'item number', 'part no', 'part number', 'barcode', 'article', 'ref'],
  name: ['name', 'product', 'product name', 'item', 'item name', 'description', 'item description', 'product description', 'desc'],
  unitPrice: ['price', 'unit price', 'selling price', 'retail price', 'price usd', 'usd', 'price (usd)', 'amount', 'cost', 'price incl vat', 'incl vat'],
  unit: ['unit', 'uom', 'unit of measure', 'units', 'pack', 'pack size', 'size'],
  category: ['category', 'dept', 'department', 'group', 'product group', 'section', 'class'],
  availableQuantity: ['qty', 'quantity', 'stock', 'on hand', 'qty on hand', 'soh', 'stock on hand', 'available', 'balance'],
  inStock: ['in stock', 'instock', 'availability', 'status', 'stock status'],
  imageUrl: ['image', 'image url', 'photo', 'picture', 'img', 'image link'],
};

const norm = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9()]+/g, ' ').trim();

/**
 * Suggest a column for each field. A saved mapping wins where its column is
 * still in the file; then exact synonym matches; then "contains" matches.
 * "Description" is a name when no better name column exists.
 */
export function guessMapping(headers, saved) {
  const mapping = {};
  const used = new Set();
  const take = (field, header) => { mapping[field] = header; used.add(header); };

  if (saved) {
    FIELDS.forEach(({ key }) => {
      if (saved[key] && headers.includes(saved[key])) take(key, saved[key]);
    });
  }
  const order = ['sku', 'unitPrice', 'unit', 'category', 'availableQuantity', 'inStock', 'imageUrl', 'name'];
  const pass = (exact) => order.forEach((field) => {
    if (mapping[field]) return;
    const hit = headers.find((h) => {
      if (used.has(h)) return false;
      const n = norm(h);
      return SYNONYMS[field].some((syn) => (exact ? n === syn : n.includes(syn)));
    });
    if (hit) take(field, hit);
  });
  pass(true);
  pass(false);
  if (!mapping.description && mapping.name) {
    const desc = headers.find((h) => !used.has(h) && /desc/.test(norm(h)));
    if (desc) take('description', desc);
  }
  return mapping;
}

/**
 * Price lists often open with a title, a date and a blank line. The header is
 * the first of the top rows with 2+ text cells, preferring one that names a
 * price or product column.
 */
export function findHeaderRow(rows) {
  const top = rows.slice(0, 15);
  const texty = (r) => (r || []).filter((c) => typeof c === 'string' && c.trim()).length;
  const known = top.findIndex((r) => texty(r) >= 2
    && (r || []).some((c) => /price|product|description|item|code/i.test(String(c))));
  if (known !== -1) return known;
  const first = top.findIndex((r) => texty(r) >= 2);
  return first === -1 ? 0 : first;
}

/** Unique, non-empty header labels ("Column C" when a cell is blank). */
export function headerLabels(row) {
  const seen = {};
  return (row || []).map((c, i) => {
    let label = String(c ?? '').trim() || `Column ${String.fromCharCode(65 + (i % 26))}`;
    if (seen[label]) label = `${label} (${(seen[label] += 1)})`;
    else seen[label] = 1;
    return label;
  });
}

/** Sheet rows under the header → [{ row, sku, name, … }], skipping blank lines. */
export function buildRows(rows, headerIndex, headers, mapping) {
  const col = {};
  Object.entries(mapping).forEach(([field, header]) => {
    const i = headers.indexOf(header);
    if (i !== -1) col[field] = i;
  });
  const out = [];
  rows.slice(headerIndex + 1).forEach((r, i) => {
    if (!r || r.every((c) => c === null || c === undefined || String(c).trim() === '')) return;
    const item = { row: headerIndex + i + 2 };
    Object.entries(col).forEach(([field, idx]) => {
      const v = r[idx];
      item[field] = v === undefined || v === null ? '' : v;
    });
    out.push(item);
  });
  return out;
}

export const TEMPLATE_CSV = [
  'Product code,Product name,Category,Unit,Price (USD),Quantity in stock',
  'CEM325,Portland Cement 32.5R 50kg,Cement & Concrete,bag,11.50,400',
  'BRK-COM,Common Brick,Bricks & Blocks,each,0.12,20000',
  'IBR-0.47-3.6,IBR Roofing Sheet 0.47mm 3.6m,Roofing Materials,sheet,18.90,120',
  'PVA-20L,PVA Paint White 20L,Paint & Finishes,tin,42.00,35',
].join('\n');
